import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import './pwa-styles.css';
import './styles/accessibility.css';
import './styles/mobile.css';
import './styles/medical-theme.css';
import { useNavigationMode, useSkipLinks } from './hooks/useAccessibility';
import { useMobileOptimization } from './hooks/useMobileOptimization';
import { useDevice } from './hooks/useDevice';
import { detectDevice, isMobile, isIOS, optimizeViewport } from './utils/device';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy load page components for code splitting
const Homepage = React.lazy(() => import('./pages/homepage/Homepage'));
const AnalysisApp = React.lazy(() => import('./pages/AnalysisApp'));

// Admin components
const AdminLogin = React.lazy(() => import('./pages/admin/Login'));
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const Institutions = React.lazy(() => import('./pages/admin/Institutions'));
const InstitutionDetail = React.lazy(() => import('./pages/admin/InstitutionDetail'));
const Users = React.lazy(() => import('./pages/admin/Users'));
const UserDetail = React.lazy(() => import('./pages/admin/UserDetail'));
const AnalysisRecords = React.lazy(() => import('./pages/admin/AnalysisRecords'));
const PaymentPackages = React.lazy(() => import('./pages/admin/PaymentPackages'));
const PaymentOrders = React.lazy(() => import('./pages/admin/PaymentOrders'));
const Receipts = React.lazy(() => import('./pages/admin/Receipts'));
const UserFeedback = React.lazy(() => import('./pages/admin/UserFeedback'));
const Settings = React.lazy(() => import('./pages/admin/Settings'));
const Help = React.lazy(() => import('./pages/admin/Help'));

// Loading component for suspense fallback
const LoadingSpinner: React.FC = () => (
  <div className="loading-container">
    <div className="loading-spinner" />
    <p>Loading...</p>
  </div>
);

const App: React.FC = () => {
  // 初始化设备检测
  useDevice();
  
  // Initialize accessibility features
  useNavigationMode();
  useSkipLinks();
  
  // Initialize mobile optimizations
  const { isMobile, isLowBattery, isSlowConnection } = useMobileOptimization();

  useEffect(() => {
    // 设置视口
    optimizeViewport();
    
    // 设置主题色
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute('content', '#2563eb');
    }

    // 添加设备类别到body
    const deviceType = detectDevice();
    document.body.className = `device-${deviceType.type} ${deviceType.mobile ? 'mobile' : 'desktop'}`;
    
    // Add mobile optimization classes
    if (isMobile) {
      document.body.classList.add('mobile-optimized');
    }
    if (isLowBattery) {
      document.body.classList.add('low-battery');
    }
    if (isSlowConnection) {
      document.body.classList.add('slow-connection');
    }
    
    // iOS Safari特殊处理
    if (isIOS() && isMobile) {
      document.body.classList.add('ios-safari');
    }

    // PWA地址栏隐藏功能
    const hideAddressBar = () => {
      // 移动端隐藏地址栏
      if (isMobile && !window.matchMedia('(display-mode: standalone)').matches) {
        setTimeout(() => {
          if (window.scrollY === 0) {
            window.scrollTo(0, 1);
            setTimeout(() => window.scrollTo(0, 0), 10);
          }
        }, 100);
      }
    };

    // 页面加载完成后隐藏地址栏
    if (document.readyState === 'complete') {
      hideAddressBar();
    } else {
      window.addEventListener('load', hideAddressBar);
    }

    // 监听屏幕旋转和窗口变化
    const handleOrientationChange = () => {
      setTimeout(hideAddressBar, 500);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    // 阻止页面弹性滚动（iOS Safari）
    const preventBounce = (e: TouchEvent) => {
      // 允许单指触摸，阻止多指触摸和缩放
      if (e.touches.length > 1) {
        e.preventDefault();
        return;
      }
      
      const element = e.target as HTMLElement;
      const scrollableParent = findScrollableParent(element);
      
      if (!scrollableParent || scrollableParent === document.body) {
        // 如果不在可滚动容器内，阻止默认行为
        const scrollY = window.scrollY;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        
        if ((scrollY <= 0 && e.touches[0].clientY > 100) || 
            (scrollY >= maxScroll && e.touches[0].clientY < window.innerHeight - 100)) {
          e.preventDefault();
        }
      }
    };

    const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
      while (element && element !== document.body) {
        const style = window.getComputedStyle(element);
        if (style.overflowY === 'scroll' || style.overflowY === 'auto') {
          return element;
        }
        element = element.parentElement as HTMLElement;
      }
      return null;
    };

    document.addEventListener('touchmove', preventBounce, { passive: false });

    return () => {
      window.removeEventListener('load', hideAddressBar);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
      document.removeEventListener('touchmove', preventBounce);
    };
  }, []);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - React 18 type compatibility issue with react-helmet-async
    <HelmetProvider>
      <div className="App">
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Homepage route */}
              <Route path="/" element={<Homepage />} />

              {/* Analysis app routes */}
              <Route path="/app/*" element={<AnalysisApp />} />

              {/* Admin login route */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Admin routes (protected) */}
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="users" element={<Users />} />
                <Route path="users/:id" element={<UserDetail />} />
                <Route path="institutions" element={<Institutions />} />
                <Route path="institutions/:id" element={<InstitutionDetail />} />
                <Route path="analysis-records" element={<AnalysisRecords />} />
                <Route path="payment-packages" element={<PaymentPackages />} />
                <Route path="payment-orders" element={<PaymentOrders />} />
                <Route path="receipts" element={<Receipts />} />
                <Route path="user-feedback" element={<UserFeedback />} />
                <Route path="settings" element={<Settings />} />
                <Route path="help" element={<Help />} />
              </Route>

              {/* Redirect old routes to new structure */}
              <Route path="/upload" element={<Navigate to="/app/upload" replace />} />
              <Route path="/query" element={<Navigate to="/app/query" replace />} />
              <Route path="/home" element={<Navigate to="/app" replace />} />

              {/* Catch-all redirect to homepage */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </div>
    </HelmetProvider>
  );
};

export default App; 